import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Modal from "./modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faClose,
  faDownload,
  faEllipsis,
  faTrashCan,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import "./image.scss";
import { GrigoraDashboardContext } from "../dashboard/context";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";
import { remove_prefix } from "../helpers";
import Pills from "./pills";
import { PulseLoader } from "react-spinners";
import InfiniteScroll from "react-infinite-scroll-component";
import { APIProviderContext } from "../api";
import { Link, useParams } from "react-router-dom";
import { debounce } from "lodash";
import TextInput from "./text";
import { Tooltip } from "../input";
import slugify from "slugify";
import { CorrectIcon } from "../../assets/ui-icons";
import Toast from "../toast/toast";
import { toast } from "react-toastify";
import { INPUT_LIMITS } from "../input-limits";

const LibraryUI = ({
  src,
  srcMode,
  onChange,
  opened,
  setOpened,
  className,
}) => {
  const { projectID } = useContext(GrigoraDashboardContext);

  const {
    listProjectImages: listProjectImagesAPI,
    deleteProjectImage: deleteProjectImageAPI,
  } = useContext(APIProviderContext);

  const [projectImagesLoaded, setProjectImagesLoaded] = useState(false);
  const [projectImages, setProjectImages] = useState({});
  const [projectImagesLastEvaluatedKey, setProjectImagesLastEvaluatedKey] =
    useState({});

  const getProjectImages = async () => {
    return await new Promise((resolve, reject) => {
      if (projectImagesLoaded && !projectImagesLastEvaluatedKey) {
        resolve();
        return;
      }
      listProjectImagesAPI(projectID, undefined, projectImagesLastEvaluatedKey)
        .then((res) => {
          var temp = {};
          res.data.Output.map((image) => {
            temp[remove_prefix(image.sk, "image-")] = image;
          });
          setProjectImagesLastEvaluatedKey(res.data.LastEvaluatedKey);
          setProjectImages({ ...projectImages, ...temp });
          setProjectImagesLoaded(true);
          resolve();
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const deleteProjectImage = async (imageID) => {
    return await new Promise((resolve, reject) => {
      deleteProjectImageAPI(projectID, imageID)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          var temp = {
            ...projectImages,
          };
          delete temp[imageID];
          setProjectImages(temp);
          resolve(res.data.Output.id);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.Output?.message);
          reject();
        });
    });
  };

  useEffect(() => {
    getProjectImages()
      .then((res) => {})
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const classes = classnames(
    "grigora-images-library-main grigora-custom-scrollbar flex flex-col justify-start select-none overflow-auto gap-7 mt-5 bg-ui-light-white h-[90vh] rounded-[21px] p-[10px]",
    className,
  );

  return (
    <>
      <div id="grigora-images-library-scrollable-area" className={classes}>
        {projectImagesLoaded ? (
          projectImages && Object.values(projectImages).length > 0 ? (
            <>
              <h1 className="modal-heading">
                Choose from the library
                <br />
              </h1>
              <InfiniteScroll
                dataLength={Object.values(projectImages)?.length}
                next={() => getProjectImages()}
                hasMore={projectImagesLastEvaluatedKey !== undefined}
                scrollableTarget="grigora-images-library-scrollable-area"
                loader={
                  <div className="flex justify-center m-2">
                    <PulseLoader size={6} color="currentColor" loading={true} />
                  </div>
                }
              >
                <div className="image-grid">
                  {[
                    ...(srcMode === "cdn" && src ? [src] : []), // if srcMode is cdn and src is not undefined, then add src to the list of images
                    ...Object.values(projectImages).filter((item) => {
                      if (srcMode === "cdn" && src) {
                        return item.name !== src.name;
                      } else {
                        return true;
                      }
                    }),
                  ].map((item) => {
                    return (
                      <ImageComponent
                        id={projectID}
                        key={item.name}
                        item={item}
                        src={src}
                        srcMode={srcMode}
                        onChange={onChange}
                        opened
                        setOpened={setOpened}
                        deleteProjectImage={deleteProjectImage}
                      />
                    );
                  })}
                </div>
              </InfiniteScroll>
            </>
          ) : (
            <>
              <div className="flex flex-col justify-center items-center w-full h-full bg-ui-light-white rounded-[21px] mt-4 mx-auto my-auto">
                <p className="text-grigora-base text-gray-700">
                  No images found
                </p>
              </div>
            </>
          )
        ) : (
          <PulseLoader size={6} color="currentColor" />
        )}
      </div>
    </>
  );
};

const ImageComponent = (props) => {
  const {
    id,
    item,
    src,
    srcMode,
    onChange,
    opened,
    setOpened,
    deleteProjectImage,
  } = props;
  const [openPopOver, setOpenPopOver] = useState(false);
  const { projectID } = useContext(GrigoraDashboardContext);

  var url;
  if (item?.resize500) {
    url = `https://cdn.grigora.co/projects/${
      item?.projectID ? item?.projectID : projectID
    }/${item.resize500}`;
  } else if (item?.og) {
    url = `https://cdn.grigora.co/projects/${
      item?.projectID ? item?.projectID : projectID
    }/${item.og}`;
  } else {
    url = `https://cdn.grigora.co/projects/${id}/${item.name}.${item.extension}`;
  }
  return (
    <>
      <div
        title={item.name}
        className={`image-component border-[5px] border-solid border-transparent ${
          item?.name === src?.name ? "!border-ui-dark-blue" : ""
        } min-w-[264px] h-[174px] cursor-pointer rounded-[21px] relative overflow-hidden`}
      >
        <img
          onClick={() => {
            onChange(
              {
                ...item,
                projectID: item.projectID ? item.projectID : projectID,
              },
              "cdn",
            );
            setOpened(false);
          }}
          loading="lazy"
          src={url}
          alt="project-images"
          className="object-cover h-full w-full "
        />

        <Popover
          placement="left"
          open={openPopOver}
          onOpenChange={setOpenPopOver}
        >
          <PopoverAnchor
            onClick={(e) => {
              setOpenPopOver(!openPopOver);
            }}
            className={`library-ui-options ${
              openPopOver ? "active" : ""
            } duration-300 absolute top-1 right-2 rotate-90 w-[20px] h-[20px] overflow-hidden !rounded-full`}
          >
            <div
              style={{
                background: openPopOver ? "#623CE7" : undefined,
              }}
              className=" hover:bg-[#623CE7] duration-300 flex items-center justify-center w-full h-full bg-slate-700"
            >
              <FontAwesomeIcon color="#fff" size="sm" icon={faEllipsis} />
            </div>
          </PopoverAnchor>
          <PopoverContent className={"image-thumbnail p-1 flex !z-[1000000]"}>
            <div
              onClick={(e) => {
                deleteProjectImage(remove_prefix(item.sk, "image-"))
                  .then((data) => {
                    setOpenPopOver(false);
                    if (item?.name === src?.name) {
                      onChange(undefined, undefined);
                    }
                  })
                  .catch((err) => {
                    console.log(err);
                    setOpenPopOver(false);
                  });
              }}
              className="bg-ui-light-white cursor-pointer duration-300 hover:bg-[#623CE7] rounded-[21px] p-1 px-2 w-24 flex justify-between items-center text-black hover:text-white"
            >
              <span className="text-sm">Delete</span>
              <FontAwesomeIcon icon={faTrashCan} size="sm" />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};

function ImageCard({ image, onChange, setOpened }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { projectID, uploadProjectImage } = useContext(GrigoraDashboardContext);

  const { downloadImage } = useContext(APIProviderContext);

  const handleImageDownload = () => {
    if (isDownloading) return;

    setIsDownloading(true);

    onChange(image.src?.original + "?auto=compress&cs=tinysrgb&w=1280", "link");
    setOpened(false);

    downloadImage(image.src?.original + "?auto=compress&cs=tinysrgb&w=1280")
      .then((data) => {
        // data is array buffer
        // Convert data to file object
        var file = new File(
          [new Blob([data])],
          slugify((image?.alt ?? "") + (image?.id ?? "")),
          { type: "image/jpeg" },
        );

        uploadProjectImage(file)
          .then((newImage) => {
            onChange(
              {
                ...newImage,
                projectID: newImage.projectID ? newImage.projectID : projectID,
              },
              "cdn",
            );
            setIsDownloading(false);
            setOpened(false);
          })
          .catch((err) => {
            setIsDownloading(false);
          });
      })
      .catch((err) => {
        setIsDownloading(false);
        console.log(err);
      });
  };

  return (
    <>
      <div className="pexels-image-component relative">
        <div className="w-full h-48">
          <img
            title={image?.alt}
            alt={image?.alt}
            src={image?.src?.medium}
            className="object-cover w-full h-full  rounded-[21px]"
          />
        </div>
        <Tooltip text="Open Image" placement="top">
          <Link
            to={image?.url}
            target="_blank"
            // onClick={handleImageDownload}
            className="download-btn absolute bottom-3 right-12 bg-gray-100 rounded-full flex justify-center items-center hover:bg-gray-300 p-2 duration-300 cursor-pointer w-[30px] h-[30px]"
          >
            <FontAwesomeIcon size="sm" icon={faArrowUp} className="rotate-45" />
          </Link>
        </Tooltip>
        <Tooltip text="Use Image" placement="top">
          <span
            onClick={handleImageDownload}
            className="download-btn absolute bottom-3 right-3 bg-gray-100 rounded-full flex justify-center items-center hover:bg-gray-300 p-2 duration-300 cursor-pointer"
          >
            <div className="w-[14px] h-[14px] flex justify-center items-center">
              {isDownloading ? (
                <>
                  <PulseLoader size={3} />
                </>
              ) : (
                <>
                  <FontAwesomeIcon size="sm" icon={faDownload} />
                </>
              )}
            </div>
          </span>
        </Tooltip>
      </div>
    </>
  );
}
function PexelsUI({ className, onChange, setOpened }) {
  const [searchText, setSearchText] = useState("");
  const [photos, setPhotos] = useState({});
  const [lastQuery, setLastQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { getPexelsImages, downloadPexelsImages } =
    useContext(APIProviderContext);

  // Fetch pexel images
  const fetchPexelsImages = (searchText) => {
    var nextPage;
    var prevPhotos;

    if (lastQuery !== searchText) {
      nextPage = 1;
      prevPhotos = {};
    } else {
      nextPage = (photos?.page ? photos?.page : 1) + 1;
      prevPhotos = photos;
    }

    getPexelsImages(searchText, nextPage)
      .then((response) => {
        setPhotos({
          ...response.data.Output.message,
          photos: [
            ...(prevPhotos?.photos ? prevPhotos?.photos : []),
            ...response.data.Output.message.photos,
          ],
        });
        setLastQuery(searchText);
      })
      .catch((err) => {
        console.log(err);
      });
    setIsLoading(false);
  };

  const onSearchChange = useCallback(
    debounce((val) => {
      fetchPexelsImages(val);
    }, 500),
    [],
  );

  useEffect(() => {
    fetchPexelsImages(searchText);
  }, []);

  const classes = classnames(
    "grigora-images-library-main grigora-custom-scrollbar flex flex-col justify-start select-none overflow-auto mt-5 bg-ui-light-white h-[90vh] rounded-[21px] p-[10px]",
    className,
  );
  return (
    <>
      <div id="grigora-images-library-scrollable-area" className={classes}>
        <>
          <TextInput
            minLength={INPUT_LIMITS["search"].minlength}
            maxLength={INPUT_LIMITS["search"].maxlength}
            isResetVisible={false}
            white={true}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              onSearchChange(e.target.value);
              setIsLoading(true);
            }}
            className="w-full pb-3 rounded-[21px] focus:outline-none text-grigora-base"
            placeholder="Search photos..."
          />
          <InfiniteScroll
            dataLength={photos?.photos?.length ?? 0}
            next={() => fetchPexelsImages(searchText)}
            hasMore={
              Math.ceil(photos?.total_results / photos?.per_page) >=
              photos?.page
                ? true
                : false
            }
            scrollableTarget="grigora-images-library-scrollable-area"
            loader={
              <div className="flex justify-center m-2">
                <PulseLoader size={6} color="currentColor" loading={true} />
              </div>
            }
          >
            {isLoading ? (
              <div className="flex justify-center m-2">
                <PulseLoader size={6} color="currentColor" loading={true} />
              </div>
            ) : (
              <></>
            )}
            <div className="image-grid">
              {photos?.photos?.length > 0 &&
                photos?.photos?.map((photo, i) => {
                  return (
                    <ImageCard
                      key={`${photo.id}-${i}`}
                      image={photo}
                      onChange={onChange}
                      setOpened={setOpened}
                    />
                  );
                })}
            </div>
          </InfiniteScroll>
        </>
      </div>
    </>
  );
}

function Pexels({ onChange, setOpened }) {
  return <PexelsUI onChange={onChange} opened setOpened={setOpened} />;
}

function AIImageUI({ onChange, setOpened }) {
  const [prompt, setPrompt] = useState("");
  const [resolution, setResolution] = useState("landscape");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageError, setImageError] = useState("");

  const { apiAction, apiAIQueueAction } = useContext(APIProviderContext);
  const { projectID, uploadProjectImage } = useContext(GrigoraDashboardContext);

  const resolutionOptions = [
    { value: "square", label: "Square" },
    { value: "portrait", label: "Portrait" },
    { value: "landscape", label: "Landscape" },
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setImageError("Please enter a prompt");
      return;
    }

    setIsGenerating(true);
    setImageError("");
    setGeneratedImage(null);

    try {
      const response = await apiAction("POST", "user/ai/details", {
        aiType: "image",
        project_id: projectID,
        message: prompt,
        resolution: resolution,
      });

      if (response?.data?.imageData) {
        // Create data URL from base64
        const dataUrl = `data:${response.data.mimeType};base64,${response.data.imageData}`;
        setGeneratedImage({
          dataUrl: dataUrl,
          base64: response.data.imageData,
          mimeType: response.data.mimeType,
          fileName: response.data.fileName,
        });
      } else {
        setImageError("Failed to generate image");
      }
    } catch (error) {
      console.error("Image generation error:", error);
      setImageError("Failed to generate image. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseImage = async () => {
    if (!generatedImage) return;

    setIsUploading(true);
    try {
      // Convert base64 to blob
      const byteCharacters = atob(generatedImage.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: generatedImage.mimeType });

      // Create a file from the blob
      const file = new File([blob], generatedImage.fileName, {
        type: generatedImage.mimeType,
      });

      // Upload to project
      const newImage = await uploadProjectImage(file);

      // Set the image and close modal
      onChange(
        {
          ...newImage,
          projectID: newImage.projectID ? newImage.projectID : projectID,
        },
        "cdn",
      );
      setOpened(false);
    } catch (error) {
      console.error("Error using generated image:", error);
      setImageError("Failed to use generated image");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col justify-start items-center w-full h-full bg-ui-light-white rounded-[21px] mt-5 mx-auto p-6 gap-4 overflow-auto">
      <h1 className="modal-heading">Generate Image with AI</h1>
      <div className="w-full max-w-md">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe the image you want to generate..."
          className="w-full h-24 px-4 py-3 rounded-[21px] border border-gray-200 focus:outline-none focus:border-ui-blue focus:ring-1 focus:ring-ui-blue resize-none"
          maxLength={500}
        />
        <div className="text-xs text-gray-500 mt-1">
          {prompt.length}/500 characters
        </div>
      </div>

      <div className="w-full max-w-md">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Resolution
        </label>
        <select
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          className="w-full px-4 py-3 rounded-[21px] border border-gray-200 focus:outline-none focus:border-ui-blue focus:ring-1 focus:ring-ui-blue"
        >
          {resolutionOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={handleGenerate}
        disabled={!prompt.trim() || isGenerating}
        className={`px-6 py-3 rounded-[21px] font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
          prompt.trim() && !isGenerating
            ? "bg-ui-blue text-white hover:bg-opacity-90"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {isGenerating ? (
          <>
            <PulseLoader color="#fff" size={12} />
            Generating...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            Generate Image
          </>
        )}
      </button>

      {imageError && (
        <div className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-[21px]">
          {imageError}
        </div>
      )}

      {generatedImage && (
        <div className="w-full max-w-md flex flex-col gap-3">
          <div className="relative">
            <img
              src={generatedImage.dataUrl}
              alt="Generated image"
              className="w-full h-auto rounded-[21px] border border-gray-200"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleUseImage}
              disabled={isUploading}
              className="flex-1 grigora-action-btn green px-4 py-2 rounded-[21px] transition-colors flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <PulseLoader color="#fff" size={8} />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} />
                  Use This Image
                </>
              )}
            </button>
            <button
              onClick={() => setGeneratedImage(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-[21px] hover:bg-gray-300 transition-colors"
            >
              Generate New
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImagePickerUIKit({ src, srcMode, onChange, opened, setOpened }) {
  const [activeTab, setActiveTab] = useState(srcMode ? srcMode : "upload"); // library and url

  useEffect(() => {
    if (srcMode) {
      setActiveTab(srcMode);
    }
  }, [srcMode]);

  const { projectID, uploadProjectImage } =
    useContext(GrigoraDashboardContext) || {};

  const handleFileInput = (e) => {
    if (!e?.target?.files?.length) {
      toast.error(<Toast title={"Error"} description={"File not found"} />);
      return;
    }

    if (e.target.files[0].size > 5 * 1024 * 1024) {
      toast.error(
        <Toast
          title={"Error"}
          description={"File size should be less than 5MB"}
        />,
      );
      return;
    }

    const url = URL.createObjectURL(e.target.files[0]);
    onChange(url, "preview");
    setOpened(false);

    if (e.target.files[0]) {
      uploadProjectImage(e.target.files[0])
        .then((newImage) => {
          onChange(
            {
              ...newImage,
              projectID: newImage.projectID ? newImage.projectID : projectID,
            },
            "cdn",
          );
          // setOpened(false);
        })
        .catch((err) => {
          onChange(undefined, undefined);
        });
    }
  };

  const inputFileRef = useRef();

  function Upload() {
    return (
      <div
        onClick={() => {
          inputFileRef.current.click();
        }}
        className="flex flex-col justify-center items-center w-full gap-4 h-full bg-ui-light-white rounded-[21px] mt-5 mx-auto my-auto"
      >
        <h1 className="modal-heading">Drag and drop your file here</h1>
        <span className="modal-heading">or</span>
        <button className="grigora-action-btn">Upload from Computer</button>
        <input
          className="hidden"
          type="file"
          ref={inputFileRef}
          onChange={handleFileInput}
          accept="image/*"
        ></input>
      </div>
    );
  }

  function Library() {
    return (
      <LibraryUI
        src={src}
        srcMode={srcMode}
        onChange={onChange}
        opened
        setOpened={setOpened}
      />
    );
  }

  const UrlUI = ({ src, srcMode, onChange, opened, setOpened }) => {
    const [link, setLink] = useState(srcMode === "link" ? src : "");
    const linkRef = useRef();

    const linkCheck = () => {
      const urlPattern =
        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
      if (urlPattern.test(link)) {
        linkRef.current.innerText = "";
        return true;
      } else {
        linkRef.current.innerText = "Not a valid link";
        return false;
      }
    };

    const handleSave = (e) => {
      e.preventDefault();
      if (linkCheck()) {
        onChange(link, "link");
        setOpened(false);
      }
    };

    return (
      <>
        <div className="flex flex-col justify-center items-center w-full h-full bg-ui-light-white rounded-[21px] mt-5 mx-auto my-auto">
          <h1 className="modal-heading">Import a file from URL</h1>
          <input
            className="rounded-[21px] md:min-w-[376px] md:w-auto w-10/12 h-[48px] outline-none px-[20px] text-sm pt-2 pb-2 pl-[30px] pr-[30px] mt-[30px] font-normal bg-white btn-shadow "
            type="text"
            value={link}
            minLength={INPUT_LIMITS["outbound-link"].minlength}
            maxLength={INPUT_LIMITS["outbound-link"].maxlength}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste a link here..."
          ></input>
          <div className="text-sm mt-4 text-red-500" ref={linkRef}></div>
          <button onClick={handleSave} className="grigora-action-btn">
            Save
          </button>
        </div>
      </>
    );
  };
  function Url() {
    return (
      <>
        <UrlUI
          src={src}
          srcMode={srcMode}
          onChange={onChange}
          opened
          setOpened={setOpened}
        />
      </>
    );
  }

  const options = [
    {
      key: "upload",
      label: "Upload",
    },
    {
      key: "cdn",
      label: "Library",
    },
    {
      key: "link",
      label: "URL",
    },
    {
      key: "ai",
      label: "AI",
    },
    {
      key: "pexels",
      label: "Pexels",
    },
  ];

  return (
    <div>
      <Modal
        isOpened={opened}
        onOutsideClick={() => {
          setOpened(false);
          setActiveTab(srcMode ? srcMode : "upload");
        }}
      >
        <div className="bg-white rounded-[21px] flex flex-col mw-[90vw] mh-[90vh] md:w-[66vw] w-[90vw] md:h-[90vh] h-[70vh] p-5 cursor-default">
          <div className=" flex items-center md:justify-between justify-around">
            <div className="flex items-center gap-5">
              <h1 className="modal-heading">Images</h1>
              <div className="flex md:gap-5 gap-2">
                <Pills
                  options={options}
                  isActive={activeTab}
                  setIsActive={setActiveTab}
                />
              </div>
            </div>
            <span
              onClick={(e) => {
                e.stopPropagation();
                setOpened(false);
              }}
              className="p-2 grid cursor-pointer"
            >
              <FontAwesomeIcon icon={faClose} />
            </span>
          </div>
          {/* content */}
          {activeTab === "upload" ? (
            <Upload />
          ) : activeTab === "cdn" || activeTab === "preview" ? (
            <Library />
          ) : activeTab === "link" ? (
            <Url />
          ) : activeTab === "ai" ? (
            <AIImageUI onChange={onChange} setOpened={setOpened} />
          ) : activeTab === "pexels" ? (
            <Pexels onChange={onChange} setOpened={setOpened} />
          ) : (
            ""
          )}
        </div>
      </Modal>
    </div>
  );
}

export default ImagePickerUIKit;
